import React from 'react';

interface TimeInput24hProps {
  value: string;
  onChange: (time: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  step?: string;
}

const TimeInput24h: React.FC<TimeInput24hProps> = ({
  value,
  onChange,
  disabled = false,
  hasError = false,
  step = '60'
}) => {
  const [hours, setHours] = React.useState<string>(
    value ? value.split(':')[0] : '00'
  );
  const [minutes, setMinutes] = React.useState<string>(
    value ? value.split(':')[1] : '00'
  );

  React.useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      setHours(h);
      setMinutes(m);
    }
  }, [value]);

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let h = e.target.value;
    
    if (h === '') {
      setHours('');
      return;
    }

    const numValue = parseInt(h, 10);
    
    if (isNaN(numValue)) return;
    
    if (numValue < 0) h = '00';
    else if (numValue > 23) h = '23';
    else h = numValue.toString().padStart(2, '0');

    setHours(h);
    onChange(`${h}:${minutes}`);
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let m = e.target.value;
    
    if (m === '') {
      setMinutes('');
      return;
    }

    const numValue = parseInt(m, 10);
    
    if (isNaN(numValue)) return;
    
    if (numValue < 0) m = '00';
    else if (numValue > 59) m = '59';
    else m = numValue.toString().padStart(2, '0');

    setMinutes(m);
    onChange(`${hours}:${m}`);
  };

  const handleHoursBlur = () => {
    if (hours === '' || hours === '0') {
      setHours('00');
    }
  };

  const handleMinutesBlur = () => {
    if (minutes === '' || minutes === '0') {
      setMinutes('00');
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <input
          type="number"
          min="0"
          max="23"
          value={hours}
          onChange={handleHoursChange}
          onBlur={handleHoursBlur}
          disabled={disabled}
          placeholder="HH"
          className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-center font-mono text-lg ${
            hasError ? 'border-red-300 bg-red-50' : 'border-gray-300'
          } ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
        />
      </div>
      <span className="text-lg font-bold text-gray-500">:</span>
      <div className="flex-1">
        <input
          type="number"
          min="0"
          max="59"
          value={minutes}
          onChange={handleMinutesChange}
          onBlur={handleMinutesBlur}
          disabled={disabled}
          placeholder="MM"
          className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-center font-mono text-lg ${
            hasError ? 'border-red-300 bg-red-50' : 'border-gray-300'
          } ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
        />
      </div>
      <span className="text-sm text-gray-500 font-medium">24H</span>
    </div>
  );
};

export default TimeInput24h;
